/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { CommonModule } from '@angular/common';
import { InsightsDataService } from '../../insights-data.service';
import { Subject } from 'rxjs';
import { latencyGraph } from '../../insights-data.interface';
@Component({
  selector: 'app-latency-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './latency-graph.component.html',
  styleUrl: './latency-graph.component.scss',
})
export class LatencyGraphComponent implements OnDestroy {
  showTrendLine = false;
  chartInstance!: echarts.ECharts;
  chartInstances: any[] = [];
  weekGraphData: any = null;
  renderedChartsCount = 0;
  allChartsRendered = false;
  private readonly destroy$ = new Subject<void>();
  totalCharts = 0;
  isShow = true;
  maxLength = 15;
  splitRange = 10;
  behaviorId = 0;
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  @Input() set graphData(latencyData: latencyGraph) {
    this.behaviorId = latencyData.caseBehaviorId ?? 0;
    this.showTrendLine = latencyData.showTrendLine ?? false;
    this.setGraphOptionsDefault(latencyData, false);
    this.setGraphOptionsDefault(latencyData, true);
  }

  constructor(
    private elementRef: ElementRef,
    private insightsService: InsightsDataService
  ) {}

  setGraphOptionsDefault(latencyData: latencyGraph, showDaysInterval: boolean) {
    if (showDaysInterval) {
      this.chartInstances = [];
      this.totalCharts = 1;
      this.allChartsRendered = false;
      this.weekGraphData = [];
      const weekData = latencyData;
      const isAllDataNull =
        weekData.averageLatency?.every(val => val === null) &&
        weekData.frequency.every(val => val === null) &&
        weekData.treatmentPhases.length === 0;

      this.weekGraphData.push(weekData);
      const graphOptions = this.setGraphOptions(weekData);
      setTimeout(() => {
        const chartDom = document.getElementById(
          `chart-container-latency-${this.behaviorId}-${0}`
        );
        if (chartDom) {
          const chartInstance = echarts.init(chartDom);
          chartInstance.setOption(graphOptions);

          chartInstance.on('finished', () => {
            this.renderedChartsCount++;
            if (this.renderedChartsCount === this.totalCharts) {
              this.allChartsRendered = true;

              this.exportAllGraphs();
            }
          });
          if (!isAllDataNull) {
            this.chartInstances.push(chartInstance);
          }
        }
      }, 100);
    } else {
      const chartOptions = this.setGraphOptions(latencyData);

      const latencyDom =
        this.elementRef.nativeElement.querySelector('#latency');

      if (latencyDom) {
        const chartInstance = echarts.init(latencyDom);
        this.chartInstance = chartInstance;
        chartInstance.setOption(chartOptions);
        chartInstance.on('finished', () => {
          this.emitChartAsImage();
        });
      }
    }
  }

  setGraphOptions(latencyData: latencyGraph) {
    const hasDataY0 = latencyData.averageLatency?.some(
      val => val !== null && val !== undefined
    );

    const trendlineLatency = this.insightsService.generateTrendlineArray(
      latencyData.xLabels,
      latencyData.averageLatency || []
    );
    const trendlineFrequency = this.insightsService.generateTrendlineArray(
      latencyData.xLabels,
      latencyData.frequency
    );

    const markLines = latencyData.treatmentPhases?.map(item => {
      const color = this.insightsService.getRandomColor();
      return {
        xAxis: item.treatmentPhaseDate,
        label: {
          show: true,
          formatter: item.treatmentPhaseName,
          position: 'insideEndTop',
          color,
        },
        lineStyle: {
          type: 'solid',
          color,
          width: 2,
        },
        tooltip: {
          show: true,
          formatter: () =>
            `Treatment Phase <br>${item.dateLabel} - ${item.treatmentPhaseName}`,
        },
      };
    });

    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minValueSpan: null,
        maxValueSpan: null,
        start: 0,
        end: 100,
      },
    ];

    const latencyGraphOption = {
      title: {
        text: 'Latency',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
      },
      legend: {
        data: [
          'Average Latency',
          'Frequency',
          'Trendline Latency',
          'Trendline Frequency',
        ],
        left: 'left',
        bottom: 40,
        selectedMode: false,
      },
      grid: {
        left: '3%',
        right: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: latencyData.xLabels,
      },
      yAxis: [
        {
          type: 'value',
          name: hasDataY0 ? 'Average Latency' : '',
          splitLine: { show: hasDataY0 },
          axisLine: { show: true },
          nameLocation: 'center',
          nameTextStyle: {
            padding: [0, 0, 10, 0],
          },
        },
        {
          type: 'value',
          name: 'Frequency',
          position: 'right',
          splitLine: { show: !hasDataY0 },
          axisLine: { show: true },
          nameLocation: 'center',
          nameTextStyle: { padding: [15, 0, 0, 0] },
        },
      ],
      series: [
        {
          name: 'Average Latency',
          type: 'bar',
          data: latencyData.averageLatency,
          barMaxWidth: 30,
          itemStyle: {
            color: '#47CD89',
            barBorderRadius: [10, 10, 0, 0],
          },
        },
        {
          name: 'Frequency',
          type: 'line',
          data: latencyData.frequency,
          yAxisIndex: 1,
          symbolSize: 12,
          symbol: 'circle',
          showSymbol: true,
          showAllSymbol: true,
          itemStyle: { color: 'orange' },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            fontSize: 10,
          },
          connectNulls: true,
        },
        {
          name: 'Target Line',
          type: 'line',
          markLine: {
            symbol: 'none',
            data: markLines,
          },
        },
        ...(this.showTrendLine
          ? [
              {
                name: 'Trendline Latency',
                type: 'line',
                data: trendlineLatency,
                yAxisIndex: 0,
                symbol: 'none',
                lineStyle: {
                  color: 'blue',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
              {
                name: 'Trendline Frequency',
                type: 'line',
                data: trendlineFrequency,
                yAxisIndex: 1,
                symbol: 'none',
                lineStyle: {
                  color: 'red',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
            ]
          : []),
      ],
      dataZoom: dataZoomConfig,
    };

    return latencyGraphOption;
  }

  emitChartAsImage() {
    const chartImageBase64 = this.insightsService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    this.chartInstances.forEach(chartInstance => {
      const base64Image =
        this.insightsService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    });

    this.allChartImage.emit(images);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
